import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import TitleSubtitle from "../../components/common/TitleSubtitle";
import { LanguageAI } from "../../types/requestsOutputs";
import { useLanguage } from "../../context/useLanguage";
import { createLanguage, deleteLanguage, getLanguages } from "../../api/administration";
import TextInput from "../../components/ui/TextInput";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { TextField } from "../../components/common/FormFields";
import FormGroup from "../../components/common/FormGroup";
import { Tooltip } from "@mui/material";
import studio from "../../styles/studioPage.module.css";
import adminSurface from "../../components/layouts/AdminSurface.module.css";
import Icon from "../../components/common/Icon";

const Languages: React.FC = () => {
    const { t: rawT } = useTranslation();
    const t = rawT as any;

    const [languages, setLanguages] = useState<LanguageAI[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [search, setSearch] = useState<string>("");
    const { language } = useLanguage();

    useEffect(() => {
        fetchLanguages();
    }, [language]);

    const fetchLanguages = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem("token");
            if (!token) {
                console.error("Token not found");
                return;
            }
            const response = await getLanguages(token);
            setLanguages(response);
        } catch (error) {
            console.error("Failed to fetch languages", error);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id: string) => {
        try {
            const token = localStorage.getItem("token");
            if (!token) {
                console.error("Token not found");
                return;
            }
            await deleteLanguage(token, id);
            setLanguages(languages.filter((language) => language.id !== id));
        } catch (error) {
            console.error("Failed to delete language", error);
        }
    };

    const validationSchema = Yup.object({
        name: Yup.string().required(t("languageNameRequired")),
        code: Yup.string()
            .matches(/^[a-zA-Z]{2}$/, t("languageCodeMustBe2Letters"))
            .required(t("languageCodeRequired")),
    });

    const filteredLanguages = languages
        .filter((language) => language.name?.toLowerCase().includes(search.toLowerCase()))
        .sort((a, b) => (a.name && b.name ? a.name.localeCompare(b.name) : 0));

    if (loading) return <div>{t("loading")}</div>;

    return (
        <div className={adminSurface.pageContent}>
            <div className={`${studio.pageTitle} ${adminSurface.pageTitle}`}>
                <TitleSubtitle title={t("languages")} subtitle={t("languagesSubTitle")} />
            </div>

            <section className={`${adminSurface.section} ${adminSurface.sectionApi}`}>
                <h2 className={adminSurface.sectionTitle}>{t("addNewLanguageForm")}</h2>

                <Formik
                    initialValues={{ name: "", code: "" }}
                    validationSchema={validationSchema}
                    onSubmit={async (values, { resetForm }) => {
                        try {
                            const token = localStorage.getItem("token");
                            if (!token) {
                                console.error("Token not found");
                                return;
                            }

                            await createLanguage(token, values.name, values.code);
                            resetForm();
                            await fetchLanguages();
                        } catch (error) {
                            console.error("Failed to add language", error);
                        }
                    }}
                    validateOnMount
                >
                    {({ isSubmitting, errors }) => (
                        <Form className={adminSurface.addForm}>
                            <div className={`${adminSurface.addFormGrid} ${adminSurface.addFormApi}`}>
                                <FormGroup name="name" title={t("enterLanguageName")} required size="small">
                                    <Field name="name" component={TextField} placeholder={t("enterLanguageName")} />
                                </FormGroup>

                                <FormGroup name="code" title={t("enterLanguageCode")} required size="small">
                                    <Field name="code" component={TextField} placeholder={t("enterLanguageCode")} />
                                </FormGroup>

                                <div>
                                    <Tooltip
                                        title={
                                            Object.keys(errors).length > 0
                                                ? t("formIncompleteTooltip", {
                                                      fields: Object.values(errors).join(", "),
                                                  })
                                                : ""
                                        }
                                        arrow
                                    >
                                        <button
                                            className={adminSurface.addButton}
                                            type="submit"
                                            disabled={isSubmitting || Object.keys(errors).length > 0}
                                        >
                                            {t("add")}
                                        </button>
                                    </Tooltip>
                                </div>
                            </div>
                        </Form>
                    )}
                </Formik>
            </section>

            <div className={adminSurface.tblBar}>
                <div className={adminSurface.searchWrap}>
                    <Icon name="ic-search" className={adminSurface.searchWrapIcon} size={18} />
                    <TextInput
                        customClassName={adminSurface.searchInput}
                        placeholder={t("searchLanguage")}
                        onChange={(e) => setSearch(e.target.value)}
                        value={search}
                        disabled={false}
                    />
                </div>
            </div>

            <div className={adminSurface.tableCard}>
                <div className={adminSurface.tblScrollBody}>
                    <table className={adminSurface.table}>
                        <thead>
                            <tr>
                                <th>{t("languageName")}</th>
                                <th>{t("languageCode")}</th>
                                <th>{t("actions")}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredLanguages.map((lang) => (
                                <tr key={lang.id}>
                                    <td>{lang.name}</td>
                                    <td>{lang.code}</td>
                                    <td>
                                        <div className="cities-table-actions">
                                            <button
                                                className={adminSurface.languagesDeleteButton}
                                                onClick={() => {
                                                    const confirmed = window.confirm(
                                                        `${t("deleteConfirmation")}: ${lang.name} id: ${lang.id}`,
                                                    );
                                                    if (confirmed) {
                                                        handleDelete(lang.id);
                                                    }
                                                }}
                                            >
                                                {t("delete")}
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <div className={adminSurface.tblFoot}>
                    <span>
                        {filteredLanguages.length} {t("languages")?.toLowerCase?.() || "languages"}
                    </span>
                </div>
            </div>
        </div>
    );
};

export default Languages;
